export interface ApiClientOptions {
  apiEndpoint: string;
  apiKey: string;
}

function normalizeBase(url: string): string {
  return (url || "").replace(/\/+$/, "");
}

function authHeaders(apiKey: string): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (apiKey) {
    headers["X-API-Key"] = apiKey;
  }
  return headers;
}

export class RateLimitError extends Error {
  retryAfter: number | null;
  constructor(message: string, retryAfter: number | null = null) {
    super(message);
    this.name = "RateLimitError";
    this.retryAfter = retryAfter;
  }
}

async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const data = await response.json();
    if (response.status === 429) {
      return (
        data.message ||
        data.detail ||
        "Too many requests. Please try again later."
      );
    }
    return data.detail || data.message || data.error || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

async function ensureOk(response: Response): Promise<void> {
  if (response.ok) return;
  const message = await parseErrorMessage(response);
  if (response.status === 429) {
    throw new RateLimitError(message);
  }
  throw new Error(message);
}

export async function postVisitorInfo(
  opts: ApiClientOptions,
  body: {
    email: string;
    contact_no?: string;
    purpose?: string;
    conversation_id?: string;
    user_id?: string;
  }
): Promise<{
  ok: boolean;
  message?: string;
  follow_up_message?: string;
  response?: string;
  calendly_url?: string;
  contact_email?: string;
  email_type?: "personal" | "organization" | string;
  user_id?: string;
  conversation_id?: string;
  detail?: string;
}> {
  const response = await fetch(
    `${normalizeBase(opts.apiEndpoint)}/visitor-info`,
    {
      method: "POST",
      headers: authHeaders(opts.apiKey),
      body: JSON.stringify(body),
    }
  );
  await ensureOk(response);
  return response.json();
}

export async function postEndSession(
  opts: ApiClientOptions,
  body: {
    conversation_id: string;
    email?: string;
    contact_no?: string;
    purpose?: string;
    user_id?: string;
  }
): Promise<void> {
  const payload = JSON.stringify(body);
  const url = `${normalizeBase(opts.apiEndpoint)}/end-session`;
  const headers = authHeaders(opts.apiKey) as Record<string, string>;

  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      // sendBeacon cannot set custom headers; fall back to fetch keepalive
    }
  } catch {
    // ignore
  }

  await fetch(url, {
    method: "POST",
    headers,
    body: payload,
    keepalive: true,
  }).catch(() => {
    // best-effort
  });
}

export type StreamHandlers = {
  onToken: (content: string) => void;
  onMetadata: (data: {
    final_response?: string;
    response?: string;
    suggestion_chips?: string[];
    conversation_id?: string;
    purpose?: string;
    request_visitor_info?: boolean;
    collect_visitor_info?: boolean;
    show_visitor_form?: boolean;
    action?: string;
  }) => void;
  onError: (message: string) => void;
};

export async function streamQuery(
  opts: ApiClientOptions,
  body: {
    query: string;
    conversation_id?: string;
    user_id?: string;
  },
  handlers: StreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(
    `${normalizeBase(opts.apiEndpoint)}/query/stream`,
    {
      method: "POST",
      headers: {
        ...authHeaders(opts.apiKey),
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      signal,
    }
  );

  if (!response.ok) {
    await ensureOk(response);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error("No stream reader available");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let eventEndIndex: number;
    while ((eventEndIndex = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.substring(0, eventEndIndex).trim();
      buffer = buffer.substring(eventEndIndex + 2);
      if (!rawEvent) continue;

      const lines = rawEvent.split("\n");
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const jsonData = JSON.parse(line.slice(6));
          if (jsonData.type === "token") {
            handlers.onToken(jsonData.content || "");
          } else if (jsonData.type === "metadata") {
            handlers.onMetadata(jsonData);
          } else if (jsonData.type === "error") {
            handlers.onError(jsonData.message || "Backend error");
          }
        } catch {
          // ignore malformed chunks
        }
      }
    }
  }
}

export function cleanResponse(text: string): string {
  if (!text) return "";
  return text
    .replace(/\[([^\]]+)\](?!\()/g, "")
    .replace(/\(Source\s+\d+\)\s*/gi, "")
    .replace(
      /Google\s*Sheets\s*\(\s*Qdrant\s+internal\s+knowledge\s+base\s*\)/gi,
      "Internal Knowledge base"
    )
    .replace(/Google\s*Sheets\s*\(\s*Qdrant[^)]*\)/gi, "Internal Knowledge base")
    .trim();
}
